import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PricingDefaultsCard — Org Settings panel for setting the default
 * early-bird / last-minute tier configuration that's inherited by NEW
 * session templates whenever an admin doesn't explicitly override.
 *
 * Persistence: stored at `organization.settings.pricing_defaults`.
 * Inheritance is one-way at template-create time — changing the
 * defaults here doesn't retroactively edit existing templates.
 */
const FIELDS = [
  { key: 'early_bird_discount',           label: '🐦 Early-bird discount (%)',           min: 0, max: 100, default: 20, isPercent: true },
  { key: 'early_bird_cutoff_days',        label: '… if booked ≥ X days ahead',           min: 0, max: 365, default: 7 },
  { key: 'last_minute_premium',           label: '⏰ Last-minute surcharge (%)',          min: 0, max: 500, default: 30, isPercent: true },
  { key: 'last_minute_premium_cutoff_days', label: '… if booked < X days ahead',         min: 0, max: 30,  default: 2 },
];

export function PricingDefaultsCard({ organization, onSaved }) {
  // Load existing defaults from the organization object; fall back to
  // the system defaults (20% / 7 days / 30% / 2 days) for the inputs.
  const buildInitial = (org) => {
    const stored = (org?.settings?.pricing_defaults) || {};
    const next = {};
    for (const f of FIELDS) {
      const v = stored[f.key];
      if (f.isPercent) {
        next[f.key] = v != null ? Math.round((v ?? f.default / 100) * 100) : f.default;
      } else {
        next[f.key] = v != null ? v : f.default;
      }
    }
    return next;
  };

  const [values, setValues] = useState(buildInitial(organization));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Re-hydrate when the organization prop changes (e.g. after parent
  // refresh post-save). Skip if dirty so we don't trample in-flight edits.
  useEffect(() => {
    if (!dirty) setValues(buildInitial(organization));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [organization]);

  const update = (key, raw) => {
    setDirty(true);
    const f = FIELDS.find((x) => x.key === key);
    let n = Number(raw);
    if (!Number.isFinite(n)) n = 0;
    n = Math.max(f.min, Math.min(f.max, n));
    setValues((v) => ({ ...v, [key]: n }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert percentage inputs back to decimals before sending.
      const payload = {};
      for (const f of FIELDS) {
        payload[f.key] = f.isPercent ? (values[f.key] || 0) / 100 : (values[f.key] || 0);
      }
      await api.updateMyOrganization({ settings: { pricing_defaults: payload } });
      toast.success('Default pricing tiers saved', {
        description: 'New session templates will inherit these values.',
      });
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleResetSystem = () => {
    setDirty(true);
    const next = {};
    FIELDS.forEach((f) => { next[f.key] = f.default; });
    setValues(next);
  };

  // Live preview using a £10 reference base price.
  const REF = 10;
  const earlyPct = values.early_bird_discount || 0;
  const latePct = values.last_minute_premium || 0;
  const isFlat = earlyPct === 0 && latePct === 0;
  const earlyPrice = (REF * (1 - earlyPct / 100)).toFixed(2);
  const latePrice = (REF * (1 + latePct / 100)).toFixed(2);

  return (
    <div className="card" style={{ padding: '1rem', marginBottom: '1.5rem' }} data-testid="pricing-defaults-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>💷 Default Pricing Tiers</h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            These values pre-fill the <strong>Pricing tiers</strong> section when you create a new Session Template. Each template can still override its own values.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={handleResetSystem}
          data-testid="pricing-defaults-reset"
          style={{ fontSize: '0.78rem', padding: '0.2rem 0.5rem' }}
        >
          Reset to system defaults
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: '0.75rem' }}>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label style={{ fontSize: '0.78rem', display: 'block' }}>{f.label}</label>
            <input
              type="number"
              min={f.min}
              max={f.max}
              step="1"
              value={values[f.key]}
              onChange={(e) => update(f.key, e.target.value)}
              data-testid={`pricing-default-${f.key}`}
            />
          </div>
        ))}
      </div>

      <div
        data-testid="pricing-defaults-preview"
        style={{
          marginTop: 8, padding: '0.45rem 0.6rem',
          background: 'hsl(var(--muted)/0.4)', borderRadius: 6,
          fontSize: '0.82rem', display: 'flex', flexWrap: 'wrap', gap: 12,
        }}
      >
        <span style={{ color: 'hsl(var(--muted-foreground))' }}>Preview at £{REF.toFixed(2)} base:</span>
        {isFlat ? (
          <span>📌 Flat <strong>£{REF.toFixed(2)}</strong> for every booking</span>
        ) : (
          <>
            <span>🐦 Early-bird: <strong>£{earlyPrice}</strong></span>
            <span>📅 Standard: <strong>£{REF.toFixed(2)}</strong></span>
            <span>⏰ Last-minute: <strong>£{latePrice}</strong></span>
          </>
        )}
      </div>

      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="pricing-defaults-save"
        >
          {saving ? 'Saving…' : (dirty ? 'Save defaults' : 'Saved')}
        </button>
      </div>
    </div>
  );
}

export default PricingDefaultsCard;
